import { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useOrders } from '../../contexts/OrdersContext';
import { getTodayCancelledOrders } from '../../utils/orderStorage';
import { getPendingReviewsCount } from '../../utils/reviewStorage';
import { AdminOrder } from '../../mocks/adminOrders';
import {
  isSoundEnabled,
  setSoundEnabled,
  playNewOrderSound,
  playCancelledOrderSound,
} from '../../utils/adminSound';
import NewOrderToast from './NewOrderToast';
import CancelledOrderToast from './CancelledOrderToast';

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { adminLogout } = useAdminAuth();
  const { totalAdminUnread } = useChat();
  const { pendingPayments, orders } = useOrders();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Sound toggle ──
  const [soundOn, setSoundOn] = useState<boolean>(isSoundEnabled);
  const [showSoundTooltip, setShowSoundTooltip] = useState(false);

  const handleToggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundEnabled(next);
    if (next) playNewOrderSound();
    setShowSoundTooltip(true);
    setTimeout(() => setShowSoundTooltip(false), 2000);
  };

  // ── New-order notification state ──
  const [toastOrders, setToastOrders] = useState<AdminOrder[]>([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const initialSeenRef = useRef<Set<string> | null>(null);
  const toastedRef = useRef<Set<string>>(new Set());

  // ── Cancelled-order notification state ──
  const [cancelledToastOrders, setCancelledToastOrders] = useState<AdminOrder[]>([]);
  const initialCancelledRef = useRef<Set<string> | null>(null);
  const cancelToastedRef = useRef<Set<string>>(new Set());

  // ── Today's cancelled badge ──
  const [todayCancelledCount, setTodayCancelledCount] = useState(0);
  const seenCancelledRef = useRef<Set<string>>(new Set());
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);

  // React to pendingPayments changes from context (replaces polling)
  useEffect(() => {
    const pendingIds = pendingPayments.map((o) => o.id);

    if (initialSeenRef.current === null) {
      initialSeenRef.current = new Set(pendingIds);
      return;
    }

    const newOrders = pendingPayments.filter(
      (o) => !initialSeenRef.current!.has(o.id) && !toastedRef.current.has(o.id)
    );
    if (newOrders.length > 0) {
      setToastOrders(newOrders);
      setUnseenCount((c) => c + newOrders.length);
      newOrders.forEach((o) => {
        toastedRef.current.add(o.id);
        initialSeenRef.current!.add(o.id);
      });
      playNewOrderSound();
    }
  }, [pendingPayments]);

  // React to cancelled orders from context
  useEffect(() => {
    const cancelled = orders.filter((o) => o.status === 'cancelled');
    const cancelledIds = cancelled.map((o) => o.id);

    if (initialCancelledRef.current === null) {
      initialCancelledRef.current = new Set(cancelledIds);
      cancelledIds.forEach((id) => seenCancelledRef.current.add(id));
      return;
    }

    const newlyCancelled = cancelled.filter(
      (o) =>
        !initialCancelledRef.current!.has(o.id) &&
        !cancelToastedRef.current.has(o.id)
    );
    if (newlyCancelled.length > 0) {
      setCancelledToastOrders(newlyCancelled);
      newlyCancelled.forEach((o) => {
        cancelToastedRef.current.add(o.id);
        initialCancelledRef.current!.add(o.id);
      });
      playCancelledOrderSound();
    }

    const todayUnseen = getTodayCancelledOrders().filter(
      (o) => !seenCancelledRef.current.has(o.id)
    );
    setTodayCancelledCount(todayUnseen.length);
  }, [orders]);

  // Poll reviews count (no context for this)
  useEffect(() => {
    setPendingReviewsCount(getPendingReviewsCount());
    const interval = setInterval(() => {
      setPendingReviewsCount(getPendingReviewsCount());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleDismissToast = useCallback(() => setToastOrders([]), []);
  const handleDismissCancelledToast = useCallback(() => setCancelledToastOrders([]), []);

  useEffect(() => {
    if (location.pathname === '/admin/payments') setUnseenCount(0);
    if (location.pathname === '/admin/orders') {
      getTodayCancelledOrders().forEach((o) => seenCancelledRef.current.add(o.id));
      setTodayCancelledCount(0);
    }
    if (location.pathname === '/admin/reviews') setPendingReviewsCount(getPendingReviewsCount());
  }, [location.pathname]);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const navItems = [
    { label: 'Orders',    path: '/admin/orders',    icon: 'ri-file-list-3-line',    badge: todayCancelledCount,                               badgeColor: 'bg-red-500'    },
    { label: 'Payments',  path: '/admin/payments',  icon: 'ri-secure-payment-line', badge: pendingPayments.length + unseenCount,              badgeColor: 'bg-orange-500' },
    { label: 'Riders',    path: '/admin/riders',    icon: 'ri-motorbike-line',      badge: 0,                                                 badgeColor: 'bg-red-500'    },
    { label: 'Customers', path: '/admin/customers', icon: 'ri-user-3-line',         badge: 0,                                                 badgeColor: 'bg-green-500'  },
    { label: 'Reviews',   path: '/admin/reviews',   icon: 'ri-chat-check-line',     badge: pendingReviewsCount,                               badgeColor: 'bg-amber-500'  },
    { label: 'Messages',  path: '/admin/messages',  icon: 'ri-chat-1-line',         badge: totalAdminUnread,                                  badgeColor: 'bg-red-500'    },
  ];

  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="ri-goblet-fill text-white text-sm" />
              </div>
              <span className="font-bold text-stone-900 text-base tracking-tight">SipDash</span>
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">Admin</span>
            </div>

            {/* Nav */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition cursor-pointer whitespace-nowrap relative ${
                      active
                        ? 'bg-orange-50 text-orange-600'
                        : 'text-stone-500 hover:bg-stone-100 hover:text-stone-800'
                    }`}
                  >
                    <i className={`${item.icon} text-sm`} />
                    {item.label}
                    {item.badge != null && item.badge > 0 && (
                      <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 ${item.badgeColor} text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse`}>
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right side: sound toggle + logout */}
          <div className="flex items-center gap-2">
            {/* Sound toggle button */}
            <div className="relative">
              <button
                onClick={handleToggleSound}
                title={soundOn ? 'Mute notifications' : 'Unmute notifications'}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition cursor-pointer whitespace-nowrap border ${
                  soundOn
                    ? 'text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-100'
                    : 'text-stone-400 bg-stone-100 border-stone-200 hover:bg-stone-200'
                }`}
              >
                <span className={`w-4 h-4 flex items-center justify-center ${soundOn ? '' : 'opacity-60'}`}>
                  <i className={`text-sm ${soundOn ? 'ri-volume-up-line' : 'ri-volume-mute-line'}`} />
                </span>
                <span className="text-xs">{soundOn ? 'Sound On' : 'Muted'}</span>
                {/* Ripple animation when sound is on */}
                {soundOn && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-400 animate-ping opacity-75" />
                )}
              </button>

              {/* Tooltip feedback */}
              {showSoundTooltip && (
                <div className="absolute top-full right-0 mt-2 z-50 bg-stone-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap pointer-events-none">
                  {soundOn ? '🔔 Sound notifications ON' : '🔕 Sound notifications OFF'}
                  <div className="absolute -top-1 right-3 w-2 h-2 bg-stone-800 rotate-45" />
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-stone-200" />

            <span className="text-sm text-stone-500">admin</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line text-sm" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {children}
      </main>

      {/* ── Floating toast notifications (stacked) ── */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-end">
        {toastOrders.length > 0 && (
          <NewOrderToast orders={toastOrders} onDismiss={handleDismissToast} />
        )}
        {cancelledToastOrders.length > 0 && (
          <CancelledOrderToast orders={cancelledToastOrders} onDismiss={handleDismissCancelledToast} />
        )}
      </div>
    </div>
  );
}
